import { Stack, Typography, Alert } from '@mui/material'
import Container from '@mui/material/Container'
import { CreateCampaignHeader } from '~/features/campaign_create/Header'
import { CreateCampaignContext } from '~/contexts/CreateCampaignContext'
import { FC, useContext, useMemo } from 'react'
import { NavigationButtons } from '../NavigationButtons'
import { useCampaignPageStepForm } from '../useCampaignPageStepForm'
import { FormProvider } from 'react-hook-form'
import {
  UseRegularlyPublishedCampaign,
  useRegularlyPublishedCampaignSchemaFields,
} from '~/features/campaign_input/UseRegularlyPublishedCampaign'
import { z } from 'zod'
import InfoIcon from '@mui/icons-material/Info'
import WarningIcon from '@mui/icons-material/Warning'
import { LinkToInfo } from '~/components/common/LinkToInfo'
import {
  OfferStartedDatePicker,
  offerStartedSchemaFields,
  validateOfferStarted,
} from '~/features/campaign_input/OfferStartedDatePicker'
import { EXTERNAL_LINKS } from '~/lib/externalLinks'
import { CreateCampaignState } from '~/features/campaign_input/state'

const schema = z
  .object({
    ...offerStartedSchemaFields,
    ...useRegularlyPublishedCampaignSchemaFields,
  })
  .superRefine(validateOfferStarted)

/**
 * 募集SNSがXのみかどうかを判定する
 * release/x-destroy で TWITTER が snsRequirements から除去されたため、
 * コピー元が X のみのキャンペーンでは全 SNS が unchecked になることを利用して判定する
 */
const isXOnlyCampaign = (snsRequirements: CreateCampaignState['snsRequirements']) => {
  return Object.values(snsRequirements).every((value) => !value.checked)
}

export const PublishSettings: FC = () => {
  const { campaign } = useContext(CreateCampaignContext)
  const isXOnly = useMemo(() => isXOnlyCampaign(campaign.snsRequirements), [campaign.snsRequirements])
  const { onSubmit, onBack, useFormMethods } = useCampaignPageStepForm({
    defaultValues: {
      publishType: campaign.publishType,
      offerStarted: campaign.offerStarted,
      useRegularlyPublishedCampaign: isXOnly ? false : campaign.useRegularlyPublishedCampaign,
    },
    schema: schema,
  })

  return (
    <form onSubmit={onSubmit}>
      <CreateCampaignHeader title="公開設定に関する情報について教えてください" onBack={onBack} />
      <Container sx={{ display: 'flex', flexDirection: 'column' }} maxWidth="sm">
        <Stack gap={5}>
          <FormProvider {...useFormMethods}>
            <OfferStartedDatePicker />
            <UseRegularlyPublishedCampaign
              disabled={isXOnly}
              disabledReason={
                <Alert severity="warning" icon={<WarningIcon />} sx={{ py: 1, mt: 1 }}>
                  X（旧Twitter）のみのキャンペーンでは定期公開を利用できません。
                </Alert>
              }
              description={
                <Alert severity="info" icon={<InfoIcon />} sx={{ py: 1, mb: 1 }}>
                  定期公開は、毎月同じ内容のキャンペーンを自動で公開できる機能です。
                  <br />
                  作成や審査の手続きを省いて、インフルエンサー募集をスムーズに継続できます。
                  <br />
                  設定はいつでも変更できるので、ぜひご活用ください！
                </Alert>
              }
              tooltip={
                <Stack gap={2}>
                  <Stack gap={1}>
                    <Typography variant="body2">
                      有効なチケットが不足している場合は、キャンペーンの定期公開は行われません。
                    </Typography>
                    <Typography variant="body2">
                      定期公開のキャンペーンも、通常同様に公開後は内容を修正できません。
                    </Typography>
                    <Typography variant="body2">
                      定期公開日（毎月の公開日）は、初回の公開時に自動で設定されます。公開後は、任意の日付に変更可能です。
                    </Typography>
                  </Stack>
                  <LinkToInfo
                    href={EXTERNAL_LINKS.REGULAR_PUBLISHING_SETTINGS}
                    linkText="こちら"
                    variant="body2"
                  />
                </Stack>
              }
            />
          </FormProvider>
        </Stack>

        <NavigationButtons />
      </Container>
    </form>
  )
}
